export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  SILENT = 5,
}

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
  showTimestamp: boolean;
  showStackTrace: boolean;
  maxStackLines: number;
  structuredLogging: boolean;
}

class Logger {
  private config: LoggerConfig;
  private readonly STORAGE_KEY = 'facade_logger_config';

  constructor() {
    // Default configuration
    const defaultConfig: LoggerConfig = {
      level: this.getDefaultLogLevel(),
      enabled: true,
      showTimestamp: true,
      showStackTrace: false,
      maxStackLines: 3,
      structuredLogging: process.env.NODE_ENV === 'production',
    };

    // Load config from localStorage or use defaults
    this.config = this.loadConfig() || defaultConfig;
  }

  private getDefaultLogLevel(): LogLevel {
    if (typeof window === 'undefined') return LogLevel.SILENT; // SSR

    // Environment-based defaults
    if (process.env.NODE_ENV === 'production') {
      return LogLevel.ERROR;
    }

    if (process.env.NODE_ENV === 'development') {
      return LogLevel.INFO;
    }

    return LogLevel.WARN;
  }

  private loadConfig(): LoggerConfig | null {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private saveConfig(): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.config));
    } catch {
      // Ignore localStorage errors
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level >= this.config.level;
  }

  private formatMessage(
    level: LogLevel,
    prefix: string,
    message: string,
    ...args: unknown[]
  ): unknown[] {
    // Use structured JSON logging in production for easier parsing
    if (this.config.structuredLogging) {
      const logEntry = {
        timestamp: new Date().toISOString(),
        level: LogLevel[level],
        category: prefix,
        message,
        data: args.length > 0 ? args : undefined,
        environment: process.env.NODE_ENV,
        app: 'web',
      };

      return [JSON.stringify(logEntry)];
    }

    // Use human-readable format in development
    const timestamp = this.config.showTimestamp
      ? new Date().toISOString().slice(11, 23)
      : '';
    const levelName = LogLevel[level];

    const formattedPrefix = `${timestamp ? `[${timestamp}] ` : ''}[${levelName}] ${prefix}`;

    return [formattedPrefix + ' ' + message, ...args];
  }

  private logWithLevel(
    level: LogLevel,
    prefix: string,
    message: string,
    ...args: unknown[]
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedArgs = this.formatMessage(level, prefix, message, ...args);

    switch (level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console -- logger internal
        console.debug(...formattedArgs);
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console -- logger internal
        console.info(...formattedArgs);
        break;
      case LogLevel.WARN:
        console.warn(...formattedArgs);
        break;
      case LogLevel.ERROR:
        console.error(...formattedArgs);
        break;
    }
  }

  // Public logging methods
  trace(prefix: string, message: string, ...args: unknown[]): void {
    this.logWithLevel(LogLevel.TRACE, prefix, message, ...args);
  }

  debug(prefix: string, message: string, ...args: unknown[]): void {
    this.logWithLevel(LogLevel.DEBUG, prefix, message, ...args);
  }

  info(prefix: string, message: string, ...args: unknown[]): void {
    this.logWithLevel(LogLevel.INFO, prefix, message, ...args);
  }

  warn(prefix: string, message: string, ...args: unknown[]): void {
    this.logWithLevel(LogLevel.WARN, prefix, message, ...args);
  }

  error(prefix: string, message: string, ...args: unknown[]): void {
    this.logWithLevel(LogLevel.ERROR, prefix, message, ...args);
  }

  // Configuration methods
  setLevel(level: LogLevel): void {
    this.config.level = level;
    this.saveConfig();
  }

  enable(): void {
    this.config.enabled = true;
    this.saveConfig();
  }

  disable(): void {
    this.config.enabled = false;
    this.saveConfig();
  }

  setShowTimestamp(show: boolean): void {
    this.config.showTimestamp = show;
    this.saveConfig();
  }

  setShowStackTrace(show: boolean): void {
    this.config.showStackTrace = show;
    this.saveConfig();
  }

  setMaxStackLines(lines: number): void {
    this.config.maxStackLines = Math.max(1, lines);
    this.saveConfig();
  }

  setStructuredLogging(enabled: boolean): void {
    this.config.structuredLogging = enabled;
    this.saveConfig();
  }

  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  // Reset to defaults
  reset(): void {
    this.config = {
      level: this.getDefaultLogLevel(),
      enabled: true,
      showTimestamp: true,
      showStackTrace: false,
      maxStackLines: 3,
      structuredLogging: process.env.NODE_ENV === 'production',
    };
    this.saveConfig();
  }
}

// Create singleton instance
const logger = new Logger();

// Create category-specific loggers
export const createLogger = (category: string) => ({
  trace: (message: string, ...args: unknown[]) =>
    logger.trace(category, message, ...args),
  debug: (message: string, ...args: unknown[]) =>
    logger.debug(category, message, ...args),
  info: (message: string, ...args: unknown[]) =>
    logger.info(category, message, ...args),
  warn: (message: string, ...args: unknown[]) =>
    logger.warn(category, message, ...args),
  error: (message: string, ...args: unknown[]) =>
    logger.error(category, message, ...args),
});

// Pre-defined loggers for common categories
export const authLogger = createLogger('AuthContext');
export const apiLogger = createLogger('API Client');
export const wsLogger = createLogger('WebSocket');
export const subscriptionLogger = createLogger('SubscriptionContext');
export const jobQueueLogger = createLogger('JobQueue');
// @deprecated Use jobQueueLogger instead
export const renderQueueLogger = jobQueueLogger;
export const interceptorLogger = createLogger('API Request');

// Export the main logger for configuration
export { logger };

// Setup window object for debugging (available in all environments for production debugging)
/* eslint-disable no-console -- CLI feedback for browser devtools users */
if (typeof window !== 'undefined') {
  (window as Window & { facade?: Record<string, unknown> }).facade = {
    ...(window as Window & { facade?: Record<string, unknown> }).facade,
    logger: {
      setLevel: (level: keyof typeof LogLevel) => {
        const levelValue = LogLevel[level];
        if (levelValue !== undefined) {
          logger.setLevel(levelValue);
          console.log(`Logger level set to ${level}`);
        }
      },
      enable: () => {
        logger.enable();
        console.log('Logger enabled');
      },
      disable: () => {
        logger.disable();
        console.log('Logger disabled');
      },
      showTimestamps: (show: boolean) => {
        logger.setShowTimestamp(show);
        console.log(`Timestamps ${show ? 'enabled' : 'disabled'}`);
      },
      showStacks: (show: boolean) => {
        logger.setShowStackTrace(show);
        console.log(`Stack traces ${show ? 'enabled' : 'disabled'}`);
      },
      setMaxStackLines: (lines: number) => {
        logger.setMaxStackLines(lines);
        console.log(`Max stack lines set to ${lines}`);
      },
      setStructuredLogging: (enabled: boolean) => {
        logger.setStructuredLogging(enabled);
        console.log(`Structured logging ${enabled ? 'enabled' : 'disabled'}`);
      },
      getConfig: () => logger.getConfig(),
      reset: () => {
        logger.reset();
        console.log('Logger reset to defaults');
      },
      enableDebug: () => {
        logger.setLevel(LogLevel.DEBUG);
        logger.setStructuredLogging(false);
        logger.setShowTimestamp(true);
        logger.enable();
        console.log('Debug mode enabled for production troubleshooting');
        console.log('Current config:', logger.getConfig());
      },
      enableWebSocketDebug: () => {
        logger.setLevel(LogLevel.DEBUG);
        logger.setStructuredLogging(false);
        logger.setShowTimestamp(true);
        logger.enable();
        console.log('WebSocket debug mode enabled');
        console.log('WebSocket events will now be logged in detail');
        console.log('Current config:', logger.getConfig());
      },
      help: () => {
        console.log(
          `
Available logger commands:

Basic controls:
  facade.logger.setLevel('TRACE' | 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'SILENT')
  facade.logger.enable() / disable()
  facade.logger.showTimestamps(true/false)
  facade.logger.showStacks(true/false)
  facade.logger.setMaxStackLines(number)
  facade.logger.setStructuredLogging(true/false)
  facade.logger.getConfig()
  facade.logger.reset()

Quick presets:
  facade.logger.enableDebug()           - Enable full debug logging
  facade.logger.enableWebSocketDebug()  - Enable WebSocket debugging

Current config:`,
          logger.getConfig()
        );
      },
    },
  };
}
/* eslint-enable no-console */
